<script setup lang="ts">
// Donut chart — pie with a hole. `inner` is a numeric fraction 0..1
// (default 0.5 = 50% inner radius). String-percent forms are
// rejected at the validator with a canonical-form migration message.

import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { DonutChartSpec } from "../sbi/protocol";
import { pieOption } from "./pie-base";
import { useChartLayout } from "./use-chart-layout";

const props = withDefaults(
  defineProps<{
    spec: DonutChartSpec;
    formats?: Record<string, string>;
  }>(),
  { formats: () => ({}) },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

const rows = computed<ReadonlyArray<Record<string, unknown>>>(() =>
  Array.isArray(props.spec.data) ? props.spec.data : [],
);

const innerPct = computed(() => {
  const inner = props.spec.inner ?? 0.5;
  return `${Math.round(Math.min(Math.max(inner, 0), 1) * 100)}%`;
});

const height = computed(() =>
  resolveChartHeight("donut", props.spec.height),
);

// Chart width picks the legend layout (side vs bottom) in
// `pieOption`; ECharts' own `autoresize` only rescales the canvas.
// Measured on `<v-chart>` itself (it must stay the template root so
// InsightChart's scoped `.chart { display: block }` reaches it),
// quantised to 16px and seeded from the embed width so the first
// option already has the right legend.
const { chartRef, ctx } = useChartLayout();

const option = computed(() =>
  pieOption({
    rows: rows.value,
    category: props.spec.category,
    value: props.spec.value,
    format: props.formats[props.spec.value],
    radius: [innerPct.value, "70%"],
    width: ctx.value.width,
    height: height.value,
    print: ctx.value.print,
  }),
);

function onClick(p: { dataIndex?: number; data?: unknown; percent?: number }) {
  if (!props.spec.id || typeof p?.dataIndex !== "number") return;
  const datum = (p.data ?? {}) as { value?: number; name?: string; folded?: boolean };
  // The folded "Other" slice isn't a category in the data — there is
  // nothing to filter on.
  if (datum.folded) return;
  emit("click-target", props.spec.id, {
    category: datum.name ?? "",
    value: datum.value ?? 0,
    percent: p.percent ?? 0,
  });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
